/**
 * Jain Gaurav Yatra — Admin payments (read-only list).
 * Backend: /jain_gaurav_yatra/admin/payments/  (cookie-session auth + X-Tenant-ID).
 *
 * The row mapper is deliberately tolerant: field names are read through a few
 * aliases so a rename on the backend doesn't blank the table.
 */
import { extractAdminTokens, getStoredAdminSession } from "@/lib/admin-login-api";

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
}

function tenantId(): string {
  return (import.meta.env.VITE_X_TENANTID as string | undefined) ?? "";
}

function authHeaders(): HeadersInit {
  const id = tenantId();
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  return {
    "Content-Type": "application/json",
    ...(id ? { "X-Tenant-ID": id } : {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
  };
}

function errMessage(data: unknown, status: number): string {
  const d = data as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" ? v : null);
  const nested = (d?.error as Record<string, unknown> | undefined) ?? undefined;
  const msg = pick(d?.message) ?? pick(d?.detail) ?? pick(nested?.message);
  if (msg) {return msg;}
  try {
    return JSON.stringify(data);
  } catch {
    return `Request failed (${status})`;
  }
}

const PAYMENTS_PATH = "/jain_gaurav_yatra/admin/payments/";

export type PaymentStatus = "success" | "pending" | "failed" | "refunded";

export const PAYMENT_STATUSES: { value: PaymentStatus; label: string }[] = [
  { value: "success", label: "Success" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
  { value: "refunded", label: "Refunded" },
];

export interface Payment {
  id: string;
  order_id: string;
  payment_id: string;
  payer_name: string;
  phone: string;
  email: string;
  amount: number;
  currency: string;
  method: string;
  status: PaymentStatus;
  booking_id: string;
  notes: string;
  created_at: string;
  /** Untouched server row, for anything the mapper doesn't surface yet. */
  raw: Record<string, unknown>;
}

function str(v: unknown): string {
  return v === null || v === undefined ? "" : String(v);
}

function num(v: unknown): number {
  if (v === null || v === undefined || v === "") {return 0;}
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function pickStr(row: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = row[k];
    if (v !== null && v !== undefined && String(v) !== "") {return String(v);}
  }
  return "";
}

/** Razorpay-style statuses collapse onto our four buckets. */
function toStatus(v: unknown): PaymentStatus {
  const s = str(v).toLowerCase();
  if (["success", "captured", "paid", "completed", "settled"].includes(s)) {return "success";}
  if (["failed", "cancelled", "canceled", "error"].includes(s)) {return "failed";}
  if (["refunded", "refund", "reversed"].includes(s)) {return "refunded";}
  return "pending";
}

function toPayment(row: Record<string, unknown>): Payment {
  return {
    id: pickStr(row, "id", "uuid", "pk", "payment_id", "razorpay_payment_id"),
    order_id: pickStr(row, "order_id", "razorpay_order_id", "receipt", "reference_id"),
    payment_id: pickStr(row, "payment_id", "razorpay_payment_id", "transaction_id"),
    payer_name: pickStr(row, "payer_name", "name", "full_name", "customer_name", "user_name"),
    phone: pickStr(row, "phone", "contact", "mobile", "phone_number"),
    email: pickStr(row, "email", "customer_email"),
    amount: num(row.amount_inr ?? row.amount ?? row.total ?? row.total_amount),
    currency: pickStr(row, "currency") || "INR",
    method: pickStr(row, "method", "payment_method", "mode"),
    status: toStatus(row.status ?? row.payment_status ?? row.state),
    booking_id: pickStr(row, "booking_id", "booking", "booking_reference"),
    notes: pickStr(row, "notes", "description", "remarks"),
    created_at: pickStr(row, "created_at", "created", "timestamp", "paid_at", "date"),
    raw: row,
  };
}

function rowsOf(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) {return data as Record<string, unknown>[];}
  const d = data as Record<string, unknown> | undefined;
  for (const key of ["results", "data", "payments"]) {
    const v = d?.[key];
    if (Array.isArray(v)) {return v as Record<string, unknown>[];}
    // Some endpoints nest the page inside `data`: { data: { results: [...] } }
    if (v && typeof v === "object") {
      const inner = (v as Record<string, unknown>).results;
      if (Array.isArray(inner)) {return inner as Record<string, unknown>[];}
    }
  }
  return [];
}

export async function listPayments(): Promise<Payment[]> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${PAYMENTS_PATH}`, {
    method: "GET",
    headers: authHeaders(),
    credentials: "include",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return rowsOf(data).map(toPayment);
}
